/** Generated-hook fragments for bounded semantic Work Episode capture. */

export function buildWorkEpisodeIntentHookFragment(): string {
  return `
function workEpisodeCaptureEnabled(value) {
  var normalized = String(value || "").trim().toLowerCase();
  return normalized === "bounded" || normalized === "on" || normalized === "true" || normalized === "1";
}

// User requests are the minimum semantic evidence needed to understand a work
// episode. Keep a small, explicitly content-bearing excerpt and redact common
// credential forms before it reaches durable state. This is opt-in at install
// time; metadata-only capture remains available.
function redactIntentText(value) {
  var text = boundedString(value, 20000);
  if (!text) return undefined;
  return text
    .replace(/\\b(?:sk|pk)-(?:live|test|lf)-[A-Za-z0-9_-]{8,}\\b/g, "[redacted-key]")
    .replace(/\\b(?:Bearer\\s+)[A-Za-z0-9._~+\\/-]{12,}=*/gi, "Bearer [redacted-token]")
    .replace(/\\b(?:ghp|github_pat|oxk)_[A-Za-z0-9_]{8,}\\b/g, "[redacted-token]")
    .replace(/[\\r\\n\\t ]+/g, " ")
    .trim()
    .slice(0, MAX_INTENT_EXCERPT);
}

function observeIntent(state, input) {
  if (!isPromptEvent(input.event) || !input.captureWorkEpisode) return;
  var excerpt = redactIntentText(input.promptText);
  if (!excerpt) return;
  state.intent_seq += 1;
  var observation = {
    ordinal: state.intent_seq,
    observed_at: input.nowIso,
    text_excerpt: excerpt,
    text_sha256: createHash("sha256").update(excerpt).digest("hex"),
    truncated: String(input.promptText || "").trim().length > excerpt.length,
    provenance: "user_asserted",
  };
  if (state.intent_observations.length < MAX_INTENT_OBSERVATIONS) {
    state.intent_observations.push(observation);
  } else {
    // Preserve the initiating request and the latest corrections/deltas.
    state.intent_observations.splice(1, 1);
    state.intent_observations.push(observation);
    state.intent_observations_omitted += 1;
  }
}

function observeLineage(state, input) {
  var parent = boundedString(input.parentSessionId, 200);
  var root = boundedString(input.rootSessionId, 200);
  var resumedFrom = boundedString(input.resumedFromSessionId, 200);
  if (!parent && !root && !resumedFrom) return;
  state.lineage = {
    ...(root ? { root_session_id: root } : {}),
    ...(parent ? { parent_session_id: parent } : {}),
    ...(resumedFrom ? { resumed_from_session_id: resumedFrom } : {}),
    provenance: "runtime_observed",
  };
}

`;
}

export function buildWorkEpisodeActionHookFragment(): string {
  return `
function safeActionDescriptor(toolName, payload, cwd) {
  var input = payload && typeof payload.tool_input === "object" && payload.tool_input
    ? payload.tool_input
    : payload && typeof payload.input === "object" && payload.input
    ? payload.input
    : {};
  var target = boundedString(payload.action_target, 240);
  var effect = boundedString(payload.action_effect, 40);
  var normalized = String(toolName || "").toLowerCase();
  if (!effect) {
    if (/read|grep|search|find|glob/.test(normalized)) effect = "inspect";
    else if (/write|edit|patch|notebook/.test(normalized)) effect = "change";
    else if (/bash|shell|terminal|exec/.test(normalized)) effect = "execute";
    else if (/browser|web|fetch|http/.test(normalized)) effect = "network";
    else effect = "invoke";
  }
  var filePath = pickString(input.file_path, input.filePath, input.path, input.notebook_path);
  if (!target && filePath) {
    try {
      var absolute = resolve(cwd || ".", filePath);
      var root = cwd ? resolve(cwd) : null;
      target = root && absolute.indexOf(root + "/") === 0
        ? "file:" + absolute.slice(root.length + 1)
        : "file:" + basename(absolute);
    } catch (e) {}
  }
  var command = pickString(input.command, payload.command);
  if (!target && command) {
    var executable = String(command).trim().split(/[\\s]+/)[0];
    var commandName = executable ? basename(executable) : null;
    if (commandName && /^(?:pnpm|npm|npx|node|git|rg|grep|find|python|python3|pytest|vitest|jest|tsc|curl|gh|make|echo)$/.test(commandName)) {
      target = "command:" + commandName;
    }
  }
  return { effect: boundedString(effect, 40), target: boundedString(target, 240) };
}

`;
}

export function buildWorkEpisodeProjectionHookFragment(): string {
  return `
function buildWorkEpisode(state, captureKind) {
  ensureExecutionState(state);
  var terminal = captureKind === "run_end" || captureKind === "session_end";
  var observations = state.intent_observations.slice(0, MAX_INTENT_OBSERVATIONS);
  return {
    schema_version: "orgx-session-work-episode/v1",
    episode_id: state.source_client + ":" + state.session_id +
      (state.state_scope === "run" && Number.isInteger(state.run_ordinal)
        ? ":run:" + state.run_ordinal
        : ""),
    source_client: state.source_client,
    source_session_id: state.session_id,
    session_kind: "user_session",
    status: terminal ? "ended" : "active",
    boundary: captureKind || "legacy_unspecified",
    started_at: state.first_ts,
    updated_at: state.last_ts,
    ...(state.lineage ? { lineage: state.lineage } : {}),
    intent: {
      observations: observations,
      observations_omitted: state.intent_observations_omitted,
      capture_complete: state.intent_observations_omitted === 0 && observations.length === state.prompts,
    },
    progress: {
      events_observed: state.events,
      prompts_observed: state.prompts,
      actions_completed_observed: state.tool_calls,
      edits_observed: state.edits,
      shell_actions_observed: state.bash,
      tools_observed: boundTools(state.tools),
    },
    provenance: {
      identity: "runtime_observed",
      intent: observations.length > 0 ? "user_asserted" : "unavailable",
      progress: "runtime_observed",
    },
    unavailable_fields: [
      ...(observations.length > 0 ? [] : ["intent"]),
      "acceptance",
      "decisions",
      "artifacts",
      "verification",
      "outcome",
      "cost",
    ],
    privacy: {
      profile: observations.length > 0 ? "bounded_work_episode" : "metadata_only",
      content_included: observations.length > 0,
      max_intent_excerpt_chars: MAX_INTENT_EXCERPT,
    },
  };
}

`;
}
